import { Component, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonComponent, ListTableComponent, ListTableDataLoader, PopupDialogComponent } from '@ppl/ui';
import { Button } from 'primeng/button';
import { map } from 'rxjs';
import { LookupService } from 'src/app/services/lookup.service';
import { UserService } from 'src/app/services/user.service';
import { ENDPOINTS } from 'src/app/shared/constants/url.constants';
import { HttpRequestService } from 'src/app/shared/services/http-request.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { formatUrl } from 'src/app/shared/utils/format-url-util';

@Component({
    imports: [FormsModule, ListTableComponent, ButtonComponent, PopupDialogComponent],
    selector: 'ppl-mass-batch-result-list',
    templateUrl: './mass-batch-result-list.component.html',
    styleUrls: ['./mass-batch-result-list.component.css']
})
export class MassBatchResultListComponent {
    @ViewChild('MassBatchResult') MassBatchResult!: ListTableComponent;
    @ViewChild(ListTableComponent) listTable!: ListTableComponent;
    programName = '';
    lazyLoad = true;
    sharedDataKey = 'list_mass_adjustments_results';
    showDeleteDialog = false;
    comments = '';
    selectedRows: any[] = [];
    deleteMessage = '';
    batchToDelete = '';
    private batchInstanceId = '';
    private batchProcessCode = '';
    private batchType = '';
    private batchStatus = '';
    private processType: any[] = [];
    jsonListPageName = '';
    buttons = [
        {
            button_id: "exclude",
            button_label: "Exclude",
            buttonAction: "onExclude",
            className: "primary"
        }
    ];
    showDialog = false;
    pageTitle = '';
    isReadOnly = false;

    constructor(
        private userService: UserService,
        private router: Router,
        private http: HttpRequestService,
        private lookupService: LookupService,
        private notificationService: NotificationService,


    ) { }

    ngAfterViewInit(): void {
        if (this.MassBatchResult) {
            this.MassBatchResult.listButton = this.buttons;
        }
    }
    onListAction(event: any) {
        const btn = event?.button;
        const selected = event?.selectedCheckBox ?? [];
        console.log('Action clicked:', btn?.buttonAction, 'selected rows:', selected);
        this.showDialog = true;
        this.selectedRows = selected;
        this.comments = '';
    }

    massAdjustmentsDataLoader: ListTableDataLoader = (payload, context) => {
        const requestPayload = this.addBatchFilter(payload);
        return this.http
            .post(ENDPOINTS.LIST_PAGE_DATA, requestPayload, context?.hasUpdatedEntry)
            .pipe(map((response) => this.ensureGridColumns(response)));
    };

    ngOnInit(): void {
        const selectedProgram = this.userService.getSelectedProgram();
        this.programName = selectedProgram?.program_code ?? '';
        this.batchInstanceId = this.router.getCurrentNavigation()?.extras?.state?.['batchInstanceId']
            ?? history.state?.batchInstanceId
            ?? '';
        this.batchProcessCode = this.router.getCurrentNavigation()?.extras?.state?.['batchProcessCode']
            ?? history.state?.batchProcessCode
            ?? '';
        this.batchType = this.router.getCurrentNavigation()?.extras?.state?.['batchType']
            ?? history.state?.batchType
            ?? '';
        this.batchStatus = this.router.getCurrentNavigation()?.extras?.state?.['batchStatus']
            ?? history.state?.batchStatus
            ?? '';

        const isAuthAdjustment = this.batchType === 'Auth Adjustment';
        const canExcludeAuthAdjustment = isAuthAdjustment && this.batchStatus === 'Awaiting Review';
        this.isReadOnly = isAuthAdjustment && !canExcludeAuthAdjustment;
        if (this.isReadOnly) {
            this.buttons = [];
        }

        if (this.batchProcessCode) {
            if (this.batchProcessCode == "I") {
                this.jsonListPageName = isAuthAdjustment
                    ? 'list_auth_adjustment_invoice_results'
                    : 'list_mass_adjust_invoice_results';
                this.pageTitle = isAuthAdjustment
                    ? 'Invoice Auth Adjustment Batch Results'
                    : 'Invoice Mass Adjustment Batch Results';
            } else if (this.batchProcessCode == "P") {
                this.jsonListPageName = 'list_mass_adjust_purchase_results';
                this.pageTitle = 'Purchase Mass Adjustment Batch Results';
            } else if (this.batchProcessCode == "T") {
                this.jsonListPageName = isAuthAdjustment
                    ? 'list_auth_adjustment_timesheet_results'
                    : 'list_mass_adjust_timesheet_results';
                this.pageTitle = isAuthAdjustment
                    ? 'Timesheet Auth Adjustment Batch Results'
                    : 'Timesheet Mass Adjustment Batch Results';
            }
        }
    }

    goBack(): void {
        this.router.navigate(['/home/bulkAdjustments']);
    }

    private addBatchFilter(payload: any): any {
        if (!this.batchInstanceId) {
            return payload;
        }

        const searchParam = Array.isArray(payload?.searchParam) ? [...payload.searchParam] : [];
        const hasBatchFilter = searchParam.some((item: any) => item?.jsonPath === 'batchInstanceId');

        if (!hasBatchFilter) {
            searchParam.push({
                jsonPath: 'batchInstanceId',
                value: this.batchInstanceId,
                operator: 'Equal'
            });
        }

        return {
            ...payload,
            searchParam
        };
    }

    private ensureGridColumns(response: any): any {
        if (Array.isArray(response?.GridColumns) && response.GridColumns.length > 0) {
            return response;
        }

        const firstRow = Array.isArray(response?.responsedata)
            ? response.responsedata.find((row: any) => row && typeof row === 'object')
            : undefined;

        if (!firstRow) {
            return response;
        }

        const preferredFields = this.batchType === 'Auth Adjustment'
            ? this.getAuthAdjustmentFieldOrder()
            : ['batchInstanceId', 'batchTypeLkp', 'batchStatusLkp', 'totalRecords', 'modifiedBy', 'modifiedDate'];
        const keys = Object.keys(firstRow);
        const orderedFields = [...preferredFields.filter((field) => keys.includes(field)), ...keys.filter((field) => !preferredFields.includes(field))];

        return {
            ...response,
            GridColumns: orderedFields.map((field) => ({
                dataColumnName: field,
                gridColumnName: this.getHeaderLabel(field)
            }))
        };
    }

    private getAuthAdjustmentFieldOrder(): string[] {
        const lineFields = this.batchProcessCode === 'I'
            ? ['invoiceLineId', 'serviceDate']
            : ['timesheetLineId', 'dateWorked'];

        return [
            'batchInstanceId',
            'participantPplId',
            ...lineFields,
            'serviceCode',
            'serviceDescription',
            'billedUnits',
            'prcocessingStatusLkp',
            'businessStatusLkp',
            'authNumber',
            'authLinkActive',
            'modifiedByName',
            'modifiedDate'
        ];
    }

    private getHeaderLabel(field: string): string {
        const headerLabels: Record<string, string> = {
            prcocessingStatusLkp: 'Processing Status',
            businessStatusLkp: 'Business Status'
        };

        if (headerLabels[field]) {
            return headerLabels[field];
        }

        return field
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (character) => character.toUpperCase());
    }

    onConfirm() {
        const payload: {
            adjustmentBatchId: number[]
            status: string,
            remarks: string
        } = {
            adjustmentBatchId: this.selectedRows.map(row => row.adjustmentBatchId),
            remarks: this.comments,
            status: "I"
        }

        const url = formatUrl(ENDPOINTS.MASS_ADJUSTMENT.RESULT_STATUS_ADJUSTMENT, {
            programCode: this.programName
        });
        this.http.patch(url, payload)
            .subscribe({
                next: (response) => {
                    this.showDialog = false;
                    this.notificationService.success('Success', 'Bulk adjustment batch updated successfully');
                    this.listTable.reloadDataFromSharedService(this.sharedDataKey);
                },
                error: (error) => {
                    this.showDialog = false;
                }
            });
    }
}
